import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Define types for our authentication context
export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  origin_app_id: string;
  is_temporary?: boolean;
  course_access?: string[]; // Array of course IDs the user has access to
}

export interface UserCourse {
  id: string;
  title: string;
  description: string;
  cover_photo: string;
  price?: number;
  created_at?: string;
}

export interface UserCoursesResponse {
  success: boolean;
  courses: UserCourse[];
  has_full_access: boolean;
  total: number;
  error?: string;
}

export interface Session {
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

interface AuthProperties {
  properties: {
    action_link?: string;
    email_otp?: string;
    hashed_token?: string;
    redirect_to?: string;
    verification_type?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface LoginWithSsoTokenResult {
  success: boolean;
  error?: string;
  isTemporary?: boolean;
  email?: string;
  user?: User;
  auth?: AuthProperties;
  course_access?: string[]; // Array of course IDs the user has access to
}

interface ClaimAccountParams {
  email: string;
  id: string;
  newPassword: string;
  name?: string;
}

interface ClaimAccountResult {
  success: boolean;
  error?: string;
}

interface RegisterParams {
  email: string;
  password: string;
  name: string;
}

interface RegisterResult {
  success: boolean;
  user?: User;
  error?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  register: (params: RegisterParams) => Promise<RegisterResult>; // New register function
  loginWithSsoToken: (ssoToken: string) => Promise<LoginWithSsoTokenResult>;
  claimAccount: (params: ClaimAccountParams) => Promise<ClaimAccountResult>;
  logout: () => void;
  error: string | null;
  hasAccessToCourse: (courseId: string) => boolean; // Method to check course access
  debugForceAccessToCourse: (courseId: string) => void; // Debug method to force access
  fetchUserCourses: () => Promise<UserCoursesResponse>; // Method to fetch user courses
  userCourses: UserCourse[]; // Array of user courses
}

// Create the authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// API constants
const API_URL = 'https://tjvbalkbmzcmmsyxaukc.supabase.co/functions/v1';
const API_HEADERS = {
  'Content-Type': 'application/json',
  'X-API-Key': 'sk_mxwOptSmxuoOC9VrsUAyFX4L1iHJ3atG',
  'X-App-ID': '12d256f3-dce5-4d04-a02b-8ed49890f8f0'
};

// Local storage keys
const USER_STORAGE_KEY = 'fitbuzz_user';
const SESSION_STORAGE_KEY = 'fitbuzz_session';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize userCourses from localStorage if available
  const [userCourses] = useState<UserCourse[]>(() => {
    try {
      const storedCoursesJson = localStorage.getItem('fitbuzz_user_courses');
      if (storedCoursesJson) {
        const storedCourses = JSON.parse(storedCoursesJson);
        if (Array.isArray(storedCourses) && storedCourses.length > 0) {
          console.log('Initializing userCourses from localStorage:', storedCourses.length, 'courses');
          return storedCourses;
        }
      }
    } catch (err) {
      console.error('Error initializing userCourses from localStorage:', err);
    }
    return [];
  });

  // Check for existing session on initial load
  useEffect(() => {
    const loadStoredAuth = () => {
      try {
        console.log('Loading authentication from storage...');
        const storedUser = localStorage.getItem(USER_STORAGE_KEY);
        const storedSession = localStorage.getItem(SESSION_STORAGE_KEY);

        if (storedUser && storedSession) {
          console.log('Found stored user and session data');
          const parsedUser = JSON.parse(storedUser) as User;
          const parsedSession = JSON.parse(storedSession) as Session;

          // Ensure the user has full access
          const userWithFullAccess = {
            ...parsedUser,
            has_full_access: true
          };

          // Check if session is expired
          const currentTime = Math.floor(Date.now() / 1000);
          if (parsedSession.expires_at > currentTime) {
            console.log('Session is valid, expires at:', new Date(parsedSession.expires_at * 1000).toLocaleString());
            setUser(userWithFullAccess);
            setSession(parsedSession);
          } else {
            console.log('Session expired at:', new Date(parsedSession.expires_at * 1000).toLocaleString());

            // If the session is expired but not by too much (less than a day), extend it
            // This helps with SSO sessions that might have short expiry times
            if (parsedSession.expires_at > (currentTime - 86400)) { // 86400 seconds = 1 day
              console.log('Session expired recently, extending it');
              const extendedSession = {
                ...parsedSession,
                expires_at: currentTime + 3600 // Extend by 1 hour
              };
              setUser(userWithFullAccess);
              setSession(extendedSession);
              localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(extendedSession));
              localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userWithFullAccess));
            } else {
              // Session expired long ago, clear storage
              console.log('Session expired too long ago, clearing storage');
              localStorage.removeItem(USER_STORAGE_KEY);
              localStorage.removeItem(SESSION_STORAGE_KEY);
            }
          }
        } else {
          console.log('No stored authentication data found');
        }
      } catch (err) {
        console.error('Error loading authentication from storage:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  // Register function
  const register = async (params: RegisterParams): Promise<RegisterResult> => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('Registering user:', params.email);

      const response = await fetch(`${API_URL}/register-user`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          email: params.email,
          password: params.password,
          name: params.name
        })
      });

      const result = await response.json();
      console.log('Registration response:', result);

      if (result.success) {
        // Registration successful
        return {
          success: true,
          user: result.user
        };
      } else {
        console.error('Registration failed:', result.message || 'Unknown error');
        setError(result.message || 'Registration failed.');
        return {
          success: false,
          error: result.message || 'Registration failed.'
        };
      }
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(`Registration failed: ${errorMessage}`);
      return {
        success: false,
        error: `Registration failed: ${errorMessage}`
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/login-user`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({ email, password })
      });

      const result = await response.json();

      if (result.success) {
        // Ensure the user has full access
        const userWithFullAccess = {
          ...result.user,
          has_full_access: true
        };

        setUser(userWithFullAccess);
        setSession(result.session);

        // Store in localStorage
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userWithFullAccess));
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(result.session));

        return true;
      } else {
        setError(result.message || 'Login failed. Please check your credentials.');
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(`Login failed: ${errorMessage}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    setSession(null);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(SESSION_STORAGE_KEY);
  };

  // SSO login function
  const loginWithSsoToken = async (ssoToken: string): Promise<LoginWithSsoTokenResult> => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('Verifying SSO token...');

      const response = await fetch(`${API_URL}/verify-sso-token`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({ sso_token: ssoToken })
      });

      const result = await response.json();
      console.log('SSO verification response:', result);

      if (result.success) {
        // Store user and auth information
        console.log('User info from SSO:', result.user);

        // All users have full access, so we don't need to check course_access
        console.log('Setting user with full access');

        // Create user object (no need to track specific course access)
        const userWithAccess = {
          ...result.user,
          has_full_access: true
        };

        console.log('User with full access:', userWithAccess);
        setUser(userWithAccess);

        // Create a session object from the auth properties
        // Ensure expires_at is a number and in the future (at least 1 hour from now)
        const currentTime = Math.floor(Date.now() / 1000);
        const expiresAt = typeof result.auth.properties.expires_at === 'number'
          ? result.auth.properties.expires_at
          : currentTime + 3600; // Default to 1 hour from now if not provided

        const newSession: Session = {
          access_token: result.auth.properties.access_token,
          refresh_token: result.auth.properties.refresh_token,
          expires_at: expiresAt > currentTime ? expiresAt : currentTime + 3600
        };

        console.log('Created session with expiry at:', new Date(newSession.expires_at * 1000).toLocaleString());

        setSession(newSession);

        // Store in localStorage
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userWithAccess));
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));

        return {
          success: true,
          isTemporary: result.user.is_temporary,
          email: result.user.email,
          user: userWithAccess,
          auth: result.auth
        };
      } else {
        console.error('SSO verification failed:', result.message || 'Unknown error');
        setError(result.message || 'SSO verification failed.');
        return {
          success: false,
          error: result.message || 'SSO verification failed.'
        };
      }
    } catch (err) {
      console.error('SSO login error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(`SSO login failed: ${errorMessage}`);
      return {
        success: false,
        error: `SSO login failed: ${errorMessage}`
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Claim account function
  const claimAccount = async (params: ClaimAccountParams): Promise<ClaimAccountResult> => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('Claiming account for email:', params.email);

      const response = await fetch(`${API_URL}/claim-account`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          email: params.email,
          id: params.id,
          new_password: params.newPassword,
          name: params.name
        })
      });

      const result = await response.json();
      console.log('Account claiming response:', result);

      if (result.success) {
        // Update user information if available
        if (result.user && user) {
          console.log('Updating user information with:', result.user);

          // Make sure to set is_temporary to false since the account is now claimed
          const updatedUser = {
            ...user,
            ...result.user,
            is_temporary: false
          };

          setUser(updatedUser);
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updatedUser));

          // Also update the session storage to reflect that this is no longer a temporary account
          const userDataJson = sessionStorage.getItem('fitbuzz_temp_user_data');
          if (userDataJson) {
            try {
              const userData = JSON.parse(userDataJson);
              userData.is_temporary = false;
              sessionStorage.setItem('fitbuzz_temp_user_data', JSON.stringify(userData));
            } catch (err) {
              console.error('Error updating user data in session storage:', err);
            }
          }
        }

        return { success: true };
      } else {
        console.error('Account claiming failed:', result.message || 'Unknown error');
        setError(result.message || 'Failed to claim account.');
        return {
          success: false,
          error: result.message || 'Failed to claim account.'
        };
      }
    } catch (err) {
      console.error('Account claiming error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(`Account claiming failed: ${errorMessage}`);
      return {
        success: false,
        error: `Account claiming failed: ${errorMessage}`
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Debug function to force access to a specific course
  const debugForceAccessToCourse = (courseId: string): void => {
    if (!user) {
      console.error('Cannot force access: No user is logged in');
      return;
    }

    console.log(`DEBUG: Forcing access to course ${courseId}`);

    // Create a new course_access array with the specified course ID
    const courseAccess = user.course_access && Array.isArray(user.course_access)
      ? [...user.course_access, courseId]
      : [courseId];

    // Make sure the course ID is only added once
    const uniqueCourseAccess = [...new Set(courseAccess)];

    // Update the user object
    const updatedUser = {
      ...user,
      course_access: uniqueCourseAccess
    };

    // Update state and localStorage
    setUser(updatedUser);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updatedUser));
    sessionStorage.setItem('fitbuzz_temp_course_access', JSON.stringify(uniqueCourseAccess));

    console.log('DEBUG: Updated user with forced course access:', updatedUser);
  };

  // Function to fetch user courses from the API
  const fetchUserCourses = useCallback(async (): Promise<UserCoursesResponse> => {
    if (!session || !session.access_token) {
      console.log('No access token available, but granting full access anyway');
      return {
        success: true,
        courses: [],
        has_full_access: true,
        total: 0
      };
    }

    try {
      console.log('Fetching user courses (simplified - all users have full access)');

      // Since all users have access to all content, we'll just return a success response
      return {
        success: true,
        courses: [],
        has_full_access: true,
        total: 0
      };
    } catch (err) {
      console.error('Error in fetchUserCourses:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      return {
        success: true, // Still return success to ensure access
        courses: [],
        has_full_access: true,
        total: 0,
        error: `Error: ${errorMessage}`
      };
    }
  }, [session]);

  // Function to check if user has access to a specific course
  const hasAccessToCourse = (courseId: string): boolean => {
    console.log('Checking access for course ID:', courseId);

    // If user is not authenticated, they don't have access
    if (!user || !session) {
      console.log('User not authenticated, access denied');
      return false;
    }

    // All authenticated users have access to all content
    console.log(`User is authenticated, granting access to course ${courseId}`);
    return true;
  };

  // Fetch user courses when user logs in
  useEffect(() => {
    if (user && session && session.access_token) {
      console.log('User authenticated, fetching courses...');
      fetchUserCourses().catch(err => {
        console.error('Error fetching user courses on login:', err);
      });
    }
  }, [user, session, fetchUserCourses]);

  // Provide the authentication context to children components
  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAuthenticated: !!user && !!session,
        isLoading,
        login,
        register,
        loginWithSsoToken,
        claimAccount,
        logout,
        error,
        hasAccessToCourse,
        debugForceAccessToCourse,
        fetchUserCourses,
        userCourses
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
